// src/engine/TranspositionTable.ts
// Transposition Table for caching search results
// Expected improvement: ~100 Elo gain

import { PrioritizedMove } from "./MoveGenerator.ts";

/**
 * Node types for transposition table entries
 * These tell us what the stored score means
 */
export enum NodeType {
  EXACT = 0,       // PV node - score is exact
  LOWER_BOUND = 1, // Cut node - score is >= stored value (beta cutoff)
  UPPER_BOUND = 2  // All node - score is <= stored value (alpha cutoff)
}

/**
 * Single entry in the transposition table
 */
export class TTEntry {
  key: bigint = 0n;              // Full Zobrist hash (for collision detection)
  depth: number = 0;              // Depth when this was searched
  score: number = 0;              // Evaluation score
  nodeType: NodeType = NodeType.EXACT;
  bestMove: PrioritizedMove | null = null;
  age: number = 0;                // Search generation (for replacement)
}

/**
 * Transposition Table Result
 */
export interface TTProbeResult {
  found: boolean;
  score?: number;
  bestMove?: PrioritizedMove;
  cutoff: boolean;
}

/**
 * Transposition Table
 * 
 * Stores previously evaluated positions to avoid re-searching them.
 * This is one of the most important optimizations in chess engines.
 * 
 * Benefits:
 * - Avoid redundant work (same position via different move orders)
 * - Move ordering (use best move from previous search)
 * - Deeper searches (saved time = more depth)
 * 
 * Expected gain: ~100 Elo
 */
export class TranspositionTable {
  private entries: TTEntry[];
  private size: number;           // Must be power of 2
  private mask: bigint;           // For fast modulo (size - 1)
  private currentAge: number = 0; // Incremented each search
  
  // Statistics
  public stats = {
    probes: 0,
    hits: 0,
    cutoffs: 0,
    stores: 0,
    collisions: 0
  };

  constructor(sizeMB: number = 64) {
    // Calculate number of entries
    const bytesPerEntry = 48; // Approximate size of TTEntry
    const numEntries = (sizeMB * 1024 * 1024) / bytesPerEntry;
    
    // Round down to nearest power of 2
    this.size = 1 << Math.floor(Math.log2(numEntries));
    this.mask = BigInt(this.size - 1);
    
    // Allocate entries
    this.entries = new Array(this.size);
    for (let i = 0; i < this.size; i++) {
      this.entries[i] = new TTEntry();
    }
    
    console.log(`[TT] Initialized: ${this.size} entries (~${sizeMB}MB)`);
  }

  /**
   * Get index from hash using fast modulo
   */
  private getIndex(hash: bigint): number {
    return Number(hash & this.mask);
  }

  /**
   * Start a new search (increment generation)
   */
  newSearch(): void {
    this.currentAge++;
    
    // Reset stats
    this.stats = {
      probes: 0,
      hits: 0,
      cutoffs: 0,
      stores: 0,
      collisions: 0
    };
  }

  /**
   * Clear all entries (use between games or position changes)
   */
  clear(): void {
    for (let i = 0; i < this.size; i++) {
      this.entries[i].key = 0n;
      this.entries[i].depth = 0;
      this.entries[i].bestMove = null;
    }
    console.log('[TT] Cleared');
  }

  /**
   * Probe the transposition table before searching
   * 
   * @param hash Position hash
   * @param depth Current search depth
   * @param alpha Alpha bound
   * @param beta Beta bound
   * @param ply Current ply (for mate score adjustment)
   * @returns Probe result with score and/or best move
   */
  probe(
    hash: bigint,
    depth: number,
    alpha: number,
    beta: number,
    ply: number = 0
  ): TTProbeResult {
    this.stats.probes++;
    
    const index = this.getIndex(hash);
    const entry = this.entries[index];
    
    // Check if this is the right position
    if (entry.key !== hash) {
      if (entry.key !== 0n) {
        this.stats.collisions++;
      }
      return { found: false, cutoff: false };
    }
    
    // Found the position
    this.stats.hits++;
    const bestMove = entry.bestMove || undefined;
    
    // Can we use the score?
    if (entry.depth >= depth) {
      let score = entry.score;
      
      // Adjust mate scores (they're ply-dependent)
      score = this.adjustMateScore(score, ply);
      
      // Check if we can cutoff based on node type
      switch (entry.nodeType) {
        case NodeType.EXACT:
          // Exact score - we can return immediately
          this.stats.cutoffs++;
          return { found: true, score, bestMove, cutoff: true };
          
        case NodeType.LOWER_BOUND:
          // Score is >= stored value (was a beta cutoff)
          if (score >= beta) {
            this.stats.cutoffs++;
            return { found: true, score, bestMove, cutoff: true };
          }
          break;
          
        case NodeType.UPPER_BOUND:
          // Score is <= stored value (was an alpha cutoff)
          if (score <= alpha) {
            this.stats.cutoffs++;
            return { found: true, score, bestMove, cutoff: true };
          }
          break;
      }
    }
    
    // Can't cutoff, but return best move for move ordering
    return { found: true, bestMove, cutoff: false };
  }

  /**
   * Store a position to the transposition table after searching
   * 
   * @param hash Position hash
   * @param depth Search depth
   * @param score Evaluation score
   * @param nodeType Type of node (exact, lower bound, upper bound)
   * @param bestMove Best move found
   * @param ply Current ply (for mate score adjustment)
   */
  store(
    hash: bigint,
    depth: number,
    score: number,
    nodeType: NodeType,
    bestMove: PrioritizedMove | null,
    ply: number = 0
  ): void {
    this.stats.stores++;
    
    const index = this.getIndex(hash);
    const entry = this.entries[index];
    
    // Adjust mate scores for storage
    score = this.storeMateScore(score, ply);
    
    // Replacement scheme - should we replace this entry?
    if (this.shouldReplace(entry, hash, depth)) {
      entry.key = hash;
      entry.depth = depth;
      entry.score = score;
      entry.nodeType = nodeType;
      entry.bestMove = bestMove;
      entry.age = this.currentAge;
    }
  }

  /**
   * Replacement scheme
   * Decides whether to replace an existing entry
   */
  private shouldReplace(entry: TTEntry, hash: bigint, depth: number): boolean {
    // Always replace if slot is empty
    if (entry.key === 0n) {
      return true;
    }
    
    // Always replace if it's the same position
    if (entry.key === hash) {
      return true;
    }
    
    // Replace if from an old search
    if (entry.age !== this.currentAge) {
      return true;
    }
    
    // Replace if new search is deeper
    if (depth >= entry.depth) {
      return true;
    }
    
    // Keep the old entry (deeper search from current generation)
    return false;
  }

  /**
   * Adjust mate scores when retrieving from TT
   * Mate scores are relative to current position, so we need to adjust them
   */
  private adjustMateScore(score: number, ply: number): number {
    const MATE_SCORE = 10000;
    
    if (score > MATE_SCORE - 1000) {
      // Mate score - bring it closer to current position
      return score - ply;
    } else if (score < -MATE_SCORE + 1000) {
      // Mated score - push it further from current position
      return score + ply;
    }
    
    return score;
  }

  /**
   * Adjust mate scores when storing to TT
   */
  private storeMateScore(score: number, ply: number): number {
    const MATE_SCORE = 10000;
    
    if (score > MATE_SCORE - 1000) {
      // Mate score - push it further from current position
      return score + ply;
    } else if (score < -MATE_SCORE + 1000) {
      // Mated score - bring it closer to current position
      return score - ply;
    }
    
    return score;
  }

  /**
   * Get statistics
   */
  getStats(): typeof this.stats {
    return { ...this.stats };
  }

  /**
   * Get hit rate percentage
   */
  getHitRate(): number {
    return this.stats.probes > 0 ? (this.stats.hits / this.stats.probes) * 100 : 0;
  }

  /**
   * Get cutoff rate percentage
   */
  getCutoffRate(): number {
    return this.stats.probes > 0 ? (this.stats.cutoffs / this.stats.probes) * 100 : 0;
  }

  /**
   * Print statistics
   */
  printStats(): void {
    const hitRate = this.getHitRate();
    const cutoffRate = this.getCutoffRate();
    
    console.log('[TT] Statistics:');
    console.log(`  Probes: ${this.stats.probes.toLocaleString()}`);
    console.log(`  Hits: ${this.stats.hits.toLocaleString()} (${hitRate.toFixed(1)}%)`);
    console.log(`  Cutoffs: ${this.stats.cutoffs.toLocaleString()} (${cutoffRate.toFixed(1)}%)`);
    console.log(`  Stores: ${this.stats.stores.toLocaleString()}`);
    console.log(`  Collisions: ${this.stats.collisions.toLocaleString()}`);
  }
}
